import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { OIDCCallbackRequest, OIDCCallbackResponse } from '@df-gate-share/shared';
import { useAuth } from '../contexts/AuthContext';
import { apiClient, getErrorMessage } from '../lib/api';
import { OIDC_STORAGE_KEYS } from '../lib/oidcSecurity';
import DFFilesLogo from '../components/DFFilesLogo';

type CallbackState = 'loading' | 'success' | 'error';

interface ErrorInfo {
  code: string;
  message: string;
}

const STEPS = [
  '認証プロバイダーからの認可コードを受信',
  'トークンを取得中...',
  'ユーザー情報を確認',
  'ログイン完了',
];

const OIDCCallback: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [searchParams] = useSearchParams();
  const hasStarted = useRef(false);

  const [callbackState, setCallbackState] = useState<CallbackState>('loading');
  const [activeStep, setActiveStep] = useState(1);
  const [errorInfo, setErrorInfo] = useState<ErrorInfo>({ code: '', message: '' });

  useEffect(() => {
    if (hasStarted.current) return;
    hasStarted.current = true;

    const handleCallback = async () => {
      const code = searchParams.get('code');
      const returnedState = searchParams.get('state');
      const error = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');

      if (error) {
        setErrorInfo({
          code: error.toUpperCase(),
          message: `認証プロバイダーからエラーが返されました。\n${errorDescription || '詳細不明'}`,
        });
        setCallbackState('error');
        return;
      }

      // CR-006: 認可開始時に保存したフローパラメータを取得し、一度きりで使い切る
      const storedState = sessionStorage.getItem(OIDC_STORAGE_KEYS.state);
      const nonce = sessionStorage.getItem(OIDC_STORAGE_KEYS.nonce);
      const codeVerifier = sessionStorage.getItem(OIDC_STORAGE_KEYS.codeVerifier);
      const provider = sessionStorage.getItem(OIDC_STORAGE_KEYS.provider);
      sessionStorage.removeItem(OIDC_STORAGE_KEYS.state);
      sessionStorage.removeItem(OIDC_STORAGE_KEYS.nonce);
      sessionStorage.removeItem(OIDC_STORAGE_KEYS.codeVerifier);
      sessionStorage.removeItem(OIDC_STORAGE_KEYS.provider);

      if (!code || !provider) {
        setErrorInfo({
          code: 'INVALID_CALLBACK',
          message: '認証コードが見つかりませんでした。\nログイン処理が正しく完了しなかった可能性があります。',
        });
        setCallbackState('error');
        return;
      }

      // CR-006: CSRF対策。返却された state と保存済み state を突合
      if (!returnedState || !storedState || returnedState !== storedState) {
        setErrorInfo({
          code: 'INVALID_STATE',
          message: '認証リクエストの検証に失敗しました（stateの不一致）。\nお手数ですが、最初からログインをやり直してください。',
        });
        setCallbackState('error');
        return;
      }

      try {
        setActiveStep(1);

        const redirectUri = `${window.location.origin}/oidc-callback`;
        const requestBody: OIDCCallbackRequest = {
          code,
          provider: provider as 'google' | 'microsoft' | 'dfgate',
          redirectUri,
          ...(codeVerifier ? { codeVerifier } : {}),
          ...(nonce ? { nonce } : {}),
        };

        const response = await apiClient.post<OIDCCallbackResponse>(
          '/api/v1/auth/oidc/callback',
          requestBody
        );

        setActiveStep(2);

        const { jwt, user } = response.data;
        login(jwt, user);

        setActiveStep(3);
        setCallbackState('success');

        setTimeout(() => {
          navigate('/files', { replace: true });
        }, 1500);
      } catch (err: unknown) {
        const message = getErrorMessage(err);
        setErrorInfo({
          code: 'OIDC_CALLBACK_FAILED',
          message: `サーバーとの通信中にエラーが発生しました。\n${message}`,
        });
        setCallbackState('error');
      }
    };

    handleCallback();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goToLogin = () => {
    navigate('/login', { replace: true });
  };

  return (
    <div className="hero-gradient">
      <div className="oidc-callback">
        <div className="oidc-callback__logo"><DFFilesLogo size={32} variant="light" /></div>

        {callbackState === 'loading' && (
          <div>
            <div className="oidc-callback__spinner" />
            <div className="oidc-callback__status">ログイン処理中...</div>
            <div className="oidc-callback__detail">
              認証情報を確認しています。<br />
              このページは自動的にリダイレクトされます。
            </div>

            <div className="oidc-callback__progress">
              {STEPS.map((step, index) => {
                let stepClass = 'oidc-callback__step';
                if (index < activeStep) stepClass += ' oidc-callback__step--completed';
                else if (index === activeStep) stepClass += ' oidc-callback__step--active';

                return (
                  <div key={index} className={stepClass}>
                    <div className="oidc-callback__step-icon">
                      {index < activeStep ? (
                        <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>check</span>
                      ) : index === activeStep ? (
                        <div className="oidc-callback__pulse" />
                      ) : null}
                    </div>
                    <span>{step}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {callbackState === 'success' && (
          <div>
            <div className="oidc-callback__success-icon"><span className="material-symbols-outlined" style={{ fontSize: '64px' }}>check_circle</span></div>
            <div className="oidc-callback__status">ログインに成功しました</div>
            <div className="oidc-callback__detail">
              まもなくホーム画面にリダイレクトします...
            </div>
          </div>
        )}

        {callbackState === 'error' && (
          <div className="oidc-callback__error">
            <div className="oidc-callback__error-icon"><span className="material-symbols-outlined" style={{ fontSize: '48px' }}>warning</span></div>
            <div className="oidc-callback__error-title">ログインに失敗しました</div>
            <div className="oidc-callback__error-message">
              {errorInfo.message.split('\n').map((line, i) => (
                <React.Fragment key={i}>
                  {line}
                  {i < errorInfo.message.split('\n').length - 1 && <br />}
                </React.Fragment>
              ))}
            </div>
            <div className="oidc-callback__error-code">
              ERROR_CODE: {errorInfo.code}
            </div>
            <button className="oidc-callback__btn-back" onClick={goToLogin}>
              ログイン画面に戻る
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default OIDCCallback;
